import { Tag, Tooltip, Text, Icon, Link, Checkbox } from "@amzn/storm-ui";
import { exclamationTriangle } from "@amzn/storm-ui-icons";
import {
    MultiSelectItemGroup,
    MultiSelectSearchPanel,
    MultiSelectSimpleBulkActionPanel,
} from "@amzn/storm-ui-multi-select";
import { isEqual } from "lodash";
import { useEffect, useMemo, useRef, useState, type ReactElement } from "react";
import { useStore } from "zustand";

import { unifiedFilterGroupingFunctions } from "../../../config/filters/unified";
import { TELEMETRY_ID } from "../../../constants/telemetry";
import { FeatureFlagsStore } from "../../../store/featureFlags";
import { unifiedFilterStore } from "../../../store/unifiedFilters";
import type { IBaseFilterOptionModel } from "../../../types/filters/common";
import type {
    AllUnifiedFilterKeys,
    FilterButtonActionButton,
} from "../../../types/filters/unified";
import {
    CoreUnifiedFilterKeys,
    unifiedFilterActionButtons,
    UnifiedTypeGroupInfo,
} from "../../../types/filters/unified";
import { UnifiedFilterReadableNames } from "../../../types/filters/unified";
import { getFilterPillLabel } from "../../../utils/filters.utils";
import { logWithTakt } from "../../../utils/telemetry.utils";
import {
    convertToItemType,
    parseStormSafeId,
    stormSafeId,
} from "../../../utils/unifiedContent.utils";

import {
    StyledFilterActionsContainer,
    StyledCloseButton,
    StyledLinkBox,
    StyledMultiSelect,
    StyledMultiSelectItem,
    StyledMultiSelectWrapper,
    StyledGroupCheckboxLabel,
    TooltipWrapper,
} from "./UnifiedFilterStyles";

const getAdditionalActionButtons = (
    filterKey: AllUnifiedFilterKeys,
): FilterButtonActionButton[] => {
    return unifiedFilterActionButtons[filterKey] ?? [];
};

const disabledTooltipText =
    "This filter option is disabled - no matching content would be found";

interface UnifiedFilterButtonProps<K extends AllUnifiedFilterKeys> {
    filterKey: K;
    onRemoveFilter?: (filterKey: K) => void;
}

const UnifiedFilterButton = <K extends AllUnifiedFilterKeys>(
    props: UnifiedFilterButtonProps<K>,
): ReactElement => {
    const { filterKey, onRemoveFilter } = props;

    const additionalActionButtons = getAdditionalActionButtons(filterKey);

    const [
        options,
        setSelectedFilters,
        setIsMetadataDirty,
        isMetadataDirty,
        deselectAll,
    ] = useStore(unifiedFilterStore, (state) => [
        state.filters[filterKey] as IBaseFilterOptionModel[],
        state.setSelectedValues,
        state.setIsMetadataDirty,
        state.isMetadataDirty,
        state.deselectAll,
    ]);

    const featureEnabledOptions = useMemo(() => {
        const featureFlags = FeatureFlagsStore.getState();
        return options.filter(
            (v) =>
                v.enablingFeatureFlag === undefined ||
                featureFlags[v.enablingFeatureFlag],
        );
    }, [options]);

    // Helper variables for maintaining the UX of what is selected
    const [selectedIds, setSelectedIds] = useState<string[]>(
        featureEnabledOptions
            .filter((o) => o.isSelected && !o.isDisabled)
            .map((o) => stormSafeId(o.id)),
    );

    // Used by the filter component to maintain a list of items matching the search query
    const [filteredItems, setFilteredItems] = useState(
        featureEnabledOptions.map(convertToItemType),
    );

    // A human readable name for our filter key, good for ids and ux components
    const readableName = UnifiedFilterReadableNames[filterKey];

    // The threshold before we add in the search bar and select all buttons
    const addBulkActions = featureEnabledOptions.length > 7;

    // How many list items we will render before cutting it off. This is to prevent lag when rendering thousands of list items
    const renderCutoff = 200;

    const previousFilters = useRef(selectedIds);
    useEffect(() => {
        const ids = featureEnabledOptions
            .filter((o) => o.isSelected && !o.isDisabled)
            .map((o) => stormSafeId(o.id));

        setSelectedIds(ids);
        previousFilters.current = ids;

        setFilteredItems(featureEnabledOptions.map(convertToItemType));
    }, [featureEnabledOptions]);

    /**
     * This component stores all of the filter changes and updates for this dropdown locally until this function is called. Once it is called, this will commit
     * the changes to the unified filter store, thus kicking off any listeners for changes
     */
    const updateFilterStoreWithSelected = () => {
        // only make updates if our filters actually changed
        if (!isEqual(selectedIds, previousFilters.current)) {
            const filtersRemoved = previousFilters.current.filter(
                (id) => !selectedIds.includes(id),
            );

            const filtersAdded = selectedIds.filter(
                (id) => !previousFilters.current.includes(id),
            );

            logWithTakt(TELEMETRY_ID.FILTER_OPTION_SELECTION, {
                filterKey,
                selectedIds,
                filtersAdded,
                filtersRemoved,
            });

            setSelectedFilters(
                filterKey,
                selectedIds.map((o) => parseStormSafeId(o)),
            );

            previousFilters.current = selectedIds;
            setIsMetadataDirty(true);
        }
    };

    // Helper function to render group items
    const renderGroupItems = (groupValues: any[]) => {
        return groupValues.map((option, idx) => {
            if (idx === renderCutoff) {
                return (
                    <Text
                        key="cutoff-message"
                        renderAs="p"
                        style={{ padding: "5px 15px" }}
                    >
                        <Icon
                            type={exclamationTriangle}
                            style={{
                                color: "orange",
                                paddingRight: 10,
                            }}
                        />
                        Only the first {renderCutoff} values are displayed,
                        please use the search bar or add more filters to narrow
                        down your results.
                    </Text>
                );
            } else if (idx > renderCutoff) {
                return null;
            }

            const unifiedOption = featureEnabledOptions.find(
                (o) => stormSafeId(o.id) === option.value,
            );
            const tag = unifiedOption?.tag;
            const tooltipText = unifiedOption?.isDisabled
                ? disabledTooltipText
                : unifiedOption?.tooltip;

            // Disable the tooltip if the option doesn't have one, or its length is 0
            const inertTooltip =
                !unifiedOption?.tooltip || unifiedOption?.tooltip?.length === 0;

            return (
                <TooltipWrapper
                    key={option.label}
                    data-testid={`filter-option-tooltip-${option.label}`}
                >
                    <Tooltip
                        position="right"
                        inert={inertTooltip}
                        nonInteractive={true}
                        disablePortal={true}
                        message={tooltipText}
                        transitionDuration={{ enter: 0, exit: 0 }}
                        trigger={
                            <div>
                                <StyledMultiSelectItem
                                    data-testid={`filter-option-item-${option.label}`}
                                    name={option.label}
                                    value={option.value ?? ""}
                                    disabled={
                                        unifiedOption?.isDisabled ?? false
                                    }
                                >
                                    <Text
                                        renderAs="span"
                                        style={{ marginRight: 5 }}
                                    >
                                        {option.label}
                                    </Text>
                                    {tag && (
                                        <Tag variant="beta" label={tag.label} />
                                    )}
                                </StyledMultiSelectItem>
                            </div>
                        }
                    />
                </TooltipWrapper>
            );
        });
    };

    return (
        <StyledMultiSelectWrapper data-testid="unified-filter-button-wrapper">
            <StyledMultiSelect
                data-testid={`unified-filter-dropdown-${filterKey}`}
                hideLabel={true}
                hasCloseButton={!!onRemoveFilter}
                onOverrideLabel={() => (
                    <span>
                        {readableName}:{" "}
                        {isMetadataDirty
                            ? "Loading..."
                            : getFilterPillLabel(featureEnabledOptions)}
                    </span>
                )}
                name={`${readableName}-filter-dropdown`}
                selectedValues={selectedIds}
                disabled={
                    !isMetadataDirty && featureEnabledOptions.length === 0
                }
                onChange={(values) => {
                    setSelectedIds(values.map((v) => `${v}`));
                }}
                onClose={() => {
                    updateFilterStoreWithSelected();
                }}
                loading={isMetadataDirty}
                withSearchPanel={
                    addBulkActions ? (
                        <MultiSelectSearchPanel
                            data-testid="filter-search-panel"
                            items={featureEnabledOptions.map(convertToItemType)}
                            options={{
                                keys: ["label"],
                                threshold: 0.2,
                                tokenize: true,
                                matchAllTokens: false,
                            }}
                            onFilterChange={setFilteredItems}
                            onFilterClear={() =>
                                setFilteredItems(
                                    featureEnabledOptions.map(
                                        convertToItemType,
                                    ),
                                )
                            }
                        />
                    ) : undefined
                }
                withBulkActionPanel={
                    <MultiSelectSimpleBulkActionPanel
                        data-testid="filter-bulk-action-panel"
                        selectedValues={selectedIds}
                        onUnSelectAll={() => {
                            setSelectedIds([]);
                        }}
                        buttonToggleThreshold={-1}
                    />
                }
            >
                {additionalActionButtons.length > 0 && (
                    <StyledFilterActionsContainer>
                        {additionalActionButtons.map(
                            (button: FilterButtonActionButton) => (
                                <StyledLinkBox
                                    key={button.label}
                                    data-testid="icm-button"
                                >
                                    <Link
                                        size="xsmall"
                                        onClick={button.onClick}
                                        external={button.external}
                                    >
                                        {button.label}
                                    </Link>
                                </StyledLinkBox>
                            ),
                        )}
                    </StyledFilterActionsContainer>
                )}
                {unifiedFilterGroupingFunctions[filterKey](filteredItems).map(
                    (group) => {
                        // Adding group level checkbox but only for type filter
                        if (filterKey === CoreUnifiedFilterKeys.TYPE) {
                            const groupItemIds = group.groupValues
                                .map((item) => item.value)
                                .filter((id): id is string => id !== undefined)
                                .filter((id) => {
                                    const option = featureEnabledOptions.find(
                                        (o) => stormSafeId(o.id) === id,
                                    );
                                    return !option?.isDisabled;
                                });

                            const selectedCount = selectedIds.filter((id) =>
                                groupItemIds.includes(id),
                            ).length;
                            const isFullySelected =
                                selectedCount === groupItemIds.length &&
                                groupItemIds.length > 0;
                            const isPartiallySelected =
                                selectedCount > 0 &&
                                selectedCount < groupItemIds.length;

                            const handleGroupToggle = () => {
                                if (isFullySelected) {
                                    // Deselect all items in group
                                    setSelectedIds((prev) =>
                                        prev.filter(
                                            (id) => !groupItemIds.includes(id),
                                        ),
                                    );
                                } else {
                                    // Select all items in group
                                    setSelectedIds((prev) => [
                                        ...new Set([...prev, ...groupItemIds]),
                                    ]);
                                }
                            };

                            const groupTooltip =
                                UnifiedTypeGroupInfo[group.groupName].tooltip;

                            const groupLabelWithCheckbox = (
                                <Tooltip
                                    transitionDuration={{
                                        enter: 0,
                                        exit: 0,
                                    }}
                                    position="right"
                                    inert={!groupTooltip}
                                    nonInteractive={true}
                                    disablePortal={true}
                                    message={groupTooltip}
                                    trigger={
                                        <div>
                                            <Checkbox
                                                id={`group-${group.groupName.replace(/\s+/g, "-").toLowerCase()}`}
                                                data-testid={`group-checkbox-${group.groupName}`}
                                                label={
                                                    <StyledGroupCheckboxLabel>
                                                        {group.groupName}
                                                    </StyledGroupCheckboxLabel>
                                                }
                                                checked={isFullySelected}
                                                indeterminate={
                                                    isPartiallySelected
                                                }
                                                onChange={handleGroupToggle}
                                                onClick={(e) =>
                                                    e.stopPropagation()
                                                }
                                            />
                                        </div>
                                    }
                                />
                            );

                            return (
                                <MultiSelectItemGroup
                                    key={group.groupName}
                                    label={groupLabelWithCheckbox}
                                    data-testid={`multi-select-item-${group.groupName}`}
                                >
                                    {renderGroupItems(group.groupValues)}
                                </MultiSelectItemGroup>
                            );
                        } else {
                            return (
                                <MultiSelectItemGroup
                                    key={group.groupName}
                                    label={group.groupName}
                                    data-testid={`multi-select-item-nocheckbox-${group.groupName}`}
                                >
                                    {renderGroupItems(group.groupValues)}
                                </MultiSelectItemGroup>
                            );
                        }
                    },
                )}
            </StyledMultiSelect>
            {onRemoveFilter && (
                <div
                    style={{
                        padding: "4px",
                        cursor: "pointer",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        marginLeft: "-16px",
                    }}
                    onClick={(e) => {
                        e.stopPropagation();
                        deselectAll(filterKey);
                        onRemoveFilter?.(filterKey);
                        setIsMetadataDirty(true);
                    }}
                >
                    <StyledCloseButton
                        data-testid="remove-filter-button"
                        disabled={
                            !isMetadataDirty &&
                            featureEnabledOptions.length === 0
                        }
                        isLoading={isMetadataDirty}
                    >
                        ×
                    </StyledCloseButton>
                </div>
            )}
        </StyledMultiSelectWrapper>
    );
};

export default UnifiedFilterButton;
